import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import MainLayout from '../layouts/MainLayout';
import { 
  Plus, 
  Trash2, 
  Upload, 
  Image as ImageIcon,
  ArrowLeft,
  Package,
  Search,
  X,
  FileText,
  Target,
  BookOpen,
  CheckCircle
} from 'lucide-react';
import * as productService from '../services/productService';
import * as categoryService from '../services/categoryService';
import { createSlug } from '../services/categoryService';
import { 
  CategoryList, 
  ProductTable
} from '../components/products';

interface Product {
  _id?: string;
  id?: string;
  slug?: { tr: string; en: string; es: string };
  categoryId?: string;
  title?: { tr: string; en: string; es: string };
  summary?: { tr: string; en: string; es: string };
  content?: {
    product_description?: { tr: string; en: string; es: string };
    technical_areas?: { tr: string; en: string; es: string };
    usage_areas?: { tr: string; en: string; es: string };
    usage_instructions?: { tr: string; en: string; es: string };
    advantages?: { tr?: string[]; en?: string[]; es?: string[] };
  };
  specs?: {
    [key: string]: string;
  };
  subProducts?: Array<{ productId: string }>;
  seo?: {
    metaTitle?: { tr: string; en: string; es: string };
    metaDescription?: { tr: string; en: string; es: string };
  };
  image?: string;
  images?: string[];
  documents?: any;
  applications?: any;
  order?: number; // Sıralama değeri
  createdAt?: string;
  updatedAt?: string;
}

interface Category {
  _id?: string;
  id?: string;
  slug?: { tr: string; en: string; es: string };
  title: { tr: string; en: string; es: string };
  description: { tr: string; en: string; es: string };
  image?: string;
  order?: number; // Sıralama değeri
  createdAt?: string;
  updatedAt?: string;
}

const ProductsCategories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<'tr' | 'en' | 'es'>('tr');
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<{ image?: File }>({});
  const [searchTerm, setSearchTerm] = useState('');
  
  // Çoklu görsel state'leri
  const [productImageFiles, setProductImageFiles] = useState<File[]>([]);
  const [productImagePreviews, setProductImagePreviews] = useState<string[]>([]);
  const [originalImages, setOriginalImages] = useState<string[]>([]); // Orijinal görseller
  const [deletedImages, setDeletedImages] = useState<string[]>([]); // Silinen görseller

  // Category form state'leri
  const [formTitles, setFormTitles] = useState({ tr: '', en: '', es: '' });
  const [formSlugs, setFormSlugs] = useState({ tr: '', en: '', es: '' });
  const [formDescriptions, setFormDescriptions] = useState({ tr: '', en: '', es: '' });
  const [categoryOrder, setCategoryOrder] = useState<number>(0);
  const [categoryNameError, setCategoryNameError] = useState('');

  // Product form state'leri
  const [productTitles, setProductTitles] = useState({ tr: '', en: '', es: '' });
  const [productSlugs, setProductSlugs] = useState({ tr: '', en: '', es: '' });
  const [productSummaries, setProductSummaries] = useState({ tr: '', en: '', es: '' });
  const [productDescriptions, setProductDescriptions] = useState({ tr: '', en: '', es: '' });
  const [productTechnicalAreas, setProductTechnicalAreas] = useState({ tr: '', en: '', es: '' });
  const [productUsageAreas, setProductUsageAreas] = useState({ tr: '', en: '', es: '' });
  const [productUsageInstructions, setProductUsageInstructions] = useState({ tr: '', en: '', es: '' });
  const [productAdvantages, setProductAdvantages] = useState({ 
    tr: [''], 
    en: [''], 
    es: [''] 
  });
  const [productSeoTitles, setProductSeoTitles] = useState({ tr: '', en: '', es: '' });
  const [productSeoDescriptions, setProductSeoDescriptions] = useState({ tr: '', en: '', es: '' });
  const [productOrder, setProductOrder] = useState<number>(0);
  const [productNameError, setProductNameError] = useState('');

  // Verileri yükle
  const fetchData = async () => {
    try {
      setLoading(true);
      const [categoriesData, productsData] = await Promise.all([
        categoryService.getCategories(),
        productService.getProducts()
      ]);
      
      console.log('Categories received:', categoriesData);
      console.log('Products received:', productsData);
      
      // Kategorileri order'a göre sırala (order yoksa alfabetik)
      const sortedCategories = categoriesData.sort((a: Category, b: Category) => {
        const orderA = a.order ?? 999999;
        const orderB = b.order ?? 999999;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        // Order aynıysa alfabetik sırala
        const titleA = (typeof a.title === 'string' ? a.title : a.title?.tr || '').toLowerCase();
        const titleB = (typeof b.title === 'string' ? b.title : b.title?.tr || '').toLowerCase();
        return titleA.localeCompare(titleB, 'tr');
      });
      
      // Ürünleri order'a göre sırala (order yoksa alfabetik)
      const sortedProducts = productsData.sort((a: Product, b: Product) => {
        const orderA = a.order ?? 999999;
        const orderB = b.order ?? 999999;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        // Order aynıysa alfabetik sırala
        const titleA = (typeof a.title === 'string' ? a.title : a.title?.tr || '').toLowerCase();
        const titleB = (typeof b.title === 'string' ? b.title : b.title?.tr || '').toLowerCase();
        return titleA.localeCompare(titleB, 'tr');
      });
      
      setCategories(sortedCategories);
      setProducts(sortedProducts);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Kategori seçildiğinde ürünleri filtrele
  const getCategoryProducts = (categoryId: string) => {
    return products.filter(product => product.categoryId === categoryId);
  };

  // Kategori düzenleme
  const handleEditCategory = (category: Category) => {
    const normalizedCategory = {
      ...category,
      id: category.id || category._id,
      title: category.title || { tr: '', en: '', es: '' },
      slug: category.slug || { tr: '', en: '', es: '' },
      description: category.description || { tr: '', en: '', es: '' }
    };
    setEditingCategory(normalizedCategory);
    setSelectedLanguage('tr'); // TR dili ile başla
    
    // Form state'lerini ayarla
    setFormTitles({
      tr: normalizedCategory.title.tr || '',
      en: normalizedCategory.title.en || '',
      es: normalizedCategory.title.es || ''
    });
    setFormSlugs({
      tr: normalizedCategory.slug.tr || '',
      en: normalizedCategory.slug.en || '',
      es: normalizedCategory.slug.es || ''
    });
    setFormDescriptions({
      tr: normalizedCategory.description.tr || '',
      en: normalizedCategory.description.en || '',
      es: normalizedCategory.description.es || ''
    });
    setCategoryOrder(normalizedCategory.order ?? 0);
    setCategoryNameError(''); // Error state'i temizle
    
    // Görsel ayarla
    if (category.image && category.image !== 'string' && category.image !== null) {
      setImagePreview(category.image);
    } else {
      setImagePreview(null);
    }
    
    setSelectedFiles({});
    setShowCategoryForm(true);
  };

  const handleNewCategory = () => {
    setEditingCategory(null);
    setSelectedLanguage('tr'); // TR dili ile başla
    setImagePreview(null);
    setSelectedFiles({});
    setFormTitles({ tr: '', en: '', es: '' });
    setFormSlugs({ tr: '', en: '', es: '' });
    setFormDescriptions({ tr: '', en: '', es: '' });
    setCategoryOrder(0);
    setCategoryNameError(''); // Error state'i temizle
    setShowCategoryForm(true);
  };

  // Görsel yönetimi
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
      
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setSelectedFiles(prev => ({ ...prev, image: file }));
    }
  };

  const handleRemoveImage = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setSelectedFiles(prev => ({ ...prev, image: undefined }));
  };

  // Çoklu görsel yönetimi fonksiyonları
  const handleProductImageSelect = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = e.target.files?.[0];
    if (file) {
      // Eski preview URL'ini temizle
      if (productImagePreviews[index] && productImagePreviews[index].startsWith('blob:')) {
        URL.revokeObjectURL(productImagePreviews[index]);
      }
      
      const previewUrl = URL.createObjectURL(file);
      
      // State'leri güncelle
      setProductImageFiles(prev => {
        const newFiles = [...prev];
        newFiles[index] = file;
        return newFiles;
      });
      
      setProductImagePreviews(prev => {
        const newPreviews = [...prev];
        newPreviews[index] = previewUrl;
        return newPreviews;
      });
    }
  };

  const handleRemoveProductImage = (index: number) => {
    const imageToRemove = productImagePreviews[index];
    
    console.log('=== REMOVING IMAGE ===');
    console.log('Index:', index);
    console.log('Image to remove:', imageToRemove);
    console.log('Original images:', originalImages);
    console.log('Current deleted images:', deletedImages);
    
    // Preview URL'ini temizle
    if (imageToRemove && imageToRemove.startsWith('blob:')) {
      URL.revokeObjectURL(imageToRemove);
      console.log('Revoked blob URL');
    }
    
    // Eğer bu orijinal bir görsel ise (URL ile başlıyorsa), silinen listesine ekle
    if (imageToRemove && !imageToRemove.startsWith('blob:') && originalImages.includes(imageToRemove)) {
      console.log('Adding to deleted images:', imageToRemove);
      setDeletedImages(prev => {
        const newDeleted = [...prev, imageToRemove];
        console.log('New deleted images list:', newDeleted);
        return newDeleted;
      });
    }
    
    // State'lerden kaldır
    setProductImageFiles(prev => prev.filter((_, i) => i !== index));
    setProductImagePreviews(prev => prev.filter((_, i) => i !== index));
    
    console.log('=== IMAGE REMOVAL COMPLETE ===');
  };

  const addNewImageSlot = () => {
    // Sınırsız görsel ekleme
    setProductImagePreviews(prev => [...prev, '']);
  };

  // Kategori ismi kontrolü
  const checkCategoryNameDuplicate = (newTitle: string) => {
    if (!newTitle.trim()) {
      setCategoryNameError('');
      return;
    }

    const existingCategory = categories.find(cat => {
      // Düzenleme modundaysa mevcut kategoriyi hariç tut
      if (editingCategory && (cat.id === editingCategory.id || cat._id === editingCategory._id)) {
        return false;
      }
      // TR başlığını karşılaştır (case insensitive)
      const existingTitle = (typeof cat.title === 'string' ? cat.title : cat.title?.tr || '').toLowerCase().trim();
      return existingTitle === newTitle.toLowerCase().trim();
    });

    if (existingCategory) {
      setCategoryNameError('Bu isimde bir kategori zaten mevcut!');
    } else {
      setCategoryNameError('');
    }
  };

  // Ürün ismi kontrolü
  const checkProductNameDuplicate = (newTitle: string) => {
    if (!newTitle.trim() || !selectedCategory) {
      setProductNameError('');
      return;
    }

    const categoryProducts = getCategoryProducts(selectedCategory?.id || selectedCategory?._id || '');
    const existingProduct = categoryProducts.find(product => {
      // Düzenleme modundaysa mevcut ürünü hariç tut
      if (editingProduct && (product.id === editingProduct.id || product._id === editingProduct._id)) {
        return false;
      }
      // TR başlığını karşılaştır (case insensitive)
      const existingTitle = (typeof product.title === 'string' ? product.title : product.title?.tr || '').toLowerCase().trim();
      return existingTitle === newTitle.toLowerCase().trim();
    });

    if (existingProduct) {
      setProductNameError('Bu kategoride aynı isimde bir ürün zaten mevcut!');
    } else {
      setProductNameError('');
    }
  };

  const handleImageUpload = async (categoryId: string) => {
    if (selectedFiles.image) {
      try {
        console.log("Uploading category image for ID:", categoryId);
        await categoryService.uploadCategoryImage(categoryId, selectedFiles.image);
        console.log("Category image uploaded successfully");
        return true;
      } catch (error) {
        console.error("Error uploading category image:", error);
        toast.error('Görsel yüklenirken hata oluştu');
        return false;
      }
    }
    return true;
  };

  // Kategori order güncelleme
  const handleUpdateCategoryOrder = async (categoryId: string, order: number) => {
    try {
      await categoryService.updateCategoryOrder(categoryId, order);
      toast.success('Kategori sıra numarası güncellendi');
      await fetchData(); // Listeyi yenile
    } catch (error) {
      console.error('Order update error:', error);
      toast.error('Sıra numarası güncellenirken hata oluştu');
    }
  };

  // Ürün order güncelleme
  const handleUpdateProductOrder = async (productId: string, order: number) => {
    try {
      await productService.updateProductOrder(productId, order);
      toast.success('Ürün sıra numarası güncellendi');
      await fetchData(); // Listeyi yenile
    } catch (error) {
      console.error('Product order update error:', error);
      toast.error('Ürün sıra numarası güncellenirken hata oluştu');
    }
  };

  // Kategori silme
  const handleDeleteCategory = async (category: Category) => {
    const categoryProducts = getCategoryProducts(category._id || category.id || '');
    
    if (categoryProducts.length > 0) {
      toast.error(`Bu kategori silinemez. İçinde ${categoryProducts.length} ürün bulunuyor.`);
      return;
    }

    if (window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      try {
        const categoryId = category._id || category.id;
        if (!categoryId) {
          toast.error('Kategori ID bulunamadı');
          return;
        }
        await categoryService.deleteCategory(categoryId);
        toast.success('Kategori başarıyla silindi');
        fetchData();
      } catch (error) {
        console.error('Error deleting category:', error);
        toast.error('Kategori silinirken hata oluştu');
      }
    }
  };

  // Ürün yönetimi
  const handleNewProduct = () => {
    if (!selectedCategory) return;
    
    setEditingProduct(null);
    setSelectedLanguage('tr'); // TR dili ile başla
    setImagePreview(null);
    setSelectedFiles({});
    
    // Çoklu görsel state'lerini temizle ve ilk slot'u ekle
    setProductImageFiles([]);
    setProductImagePreviews(['']); // İlk görsel slot'unu ekle
    setOriginalImages([]);
    setDeletedImages([]);
    
    setProductTitles({ tr: '', en: '', es: '' });
    setProductSlugs({ tr: '', en: '', es: '' }); // Slug'ları temizle
    setProductSummaries({ tr: '', en: '', es: '' });
    setProductDescriptions({ tr: '', en: '', es: '' });
    setProductTechnicalAreas({ tr: '', en: '', es: '' });
    setProductUsageAreas({ tr: '', en: '', es: '' });
    setProductUsageInstructions({ tr: '', en: '', es: '' });
    setProductAdvantages({ tr: [''], en: [''], es: [''] });
    setProductSeoTitles({ tr: '', en: '', es: '' });
    setProductSeoDescriptions({ tr: '', en: '', es: '' });
    setProductOrder(0);
    setProductNameError(''); // Error state'i temizle
    setShowProductForm(true);
  };

  const handleEditProduct = (product: Product) => {
    console.log('=== EDITING PRODUCT ===');
    console.log('Original product:', product);
    
    const normalizedProduct = {
      ...product,
      id: product.id || product._id,
      title: product.title || { tr: '', en: '', es: '' },
      summary: product.summary || { tr: '', en: '', es: '' },
      content: product.content || {
        product_description: { tr: '', en: '', es: '' },
        technical_areas: { tr: '', en: '', es: '' },
        usage_areas: { tr: '', en: '', es: '' },
        usage_instructions: { tr: '', en: '', es: '' },
        advantages: { tr: [''], en: [''], es: [''] }
      },
      specs: product.specs || {},
      seo: product.seo || {
        metaTitle: { tr: '', en: '', es: '' },
        metaDescription: { tr: '', en: '', es: '' }
      }
    };
    
    console.log('Normalized product:', normalizedProduct);
    setEditingProduct(normalizedProduct);
    setSelectedLanguage('tr'); // TR dili ile başla
    
    // Çoklu görsel state'lerini ayarla
    const existingImages = normalizedProduct.images || [];
    setProductImageFiles([]);
    setProductImagePreviews(existingImages.length > 0 ? existingImages : ['']);
    setOriginalImages(existingImages); // Orijinal görselleri sakla
    setDeletedImages([]); // Silinen görseller listesini temizle
    
    // Form state'lerini ayarla
    setProductTitles({
      tr: normalizedProduct.title.tr || '',
      en: normalizedProduct.title.en || '',
      es: normalizedProduct.title.es || ''
    });
    setProductSlugs({
      tr: normalizedProduct.slug?.tr || '',
      en: normalizedProduct.slug?.en || '',
      es: normalizedProduct.slug?.es || ''
    });
    setProductSummaries({
      tr: normalizedProduct.summary.tr || '',
      en: normalizedProduct.summary.en || '',
      es: normalizedProduct.summary.es || ''
    });
    setProductDescriptions({
      tr: normalizedProduct.content?.product_description?.tr || '',
      en: normalizedProduct.content?.product_description?.en || '',
      es: normalizedProduct.content?.product_description?.es || ''
    });
    setProductTechnicalAreas({
      tr: normalizedProduct.content?.technical_areas?.tr || '',
      en: normalizedProduct.content?.technical_areas?.en || '',
      es: normalizedProduct.content?.technical_areas?.es || ''
    });
    setProductUsageAreas({
      tr: normalizedProduct.content?.usage_areas?.tr || '',
      en: normalizedProduct.content?.usage_areas?.en || '',
      es: normalizedProduct.content?.usage_areas?.es || ''
    });
    setProductUsageInstructions({
      tr: normalizedProduct.content?.usage_instructions?.tr || '',
      en: normalizedProduct.content?.usage_instructions?.en || '',
      es: normalizedProduct.content?.usage_instructions?.es || ''
    });
    setProductAdvantages({
      tr: normalizedProduct.content?.advantages?.tr || [''],
      en: normalizedProduct.content?.advantages?.en || [''],
      es: normalizedProduct.content?.advantages?.es || ['']
    });
    setProductSeoTitles({
      tr: normalizedProduct.seo?.metaTitle?.tr || '',
      en: normalizedProduct.seo?.metaTitle?.en || '',
      es: normalizedProduct.seo?.metaTitle?.es || ''
    });
    setProductSeoDescriptions({
      tr: normalizedProduct.seo?.metaDescription?.tr || '',
      en: normalizedProduct.seo?.metaDescription?.en || '',
      es: normalizedProduct.seo?.metaDescription?.es || ''
    });
    setProductOrder(normalizedProduct.order ?? 0);
    setProductNameError(''); // Error state'i temizle
    
    console.log('Form states set:', {
      productTitles: normalizedProduct.title,
      productSummaries: normalizedProduct.summary,
      productDescriptions: normalizedProduct.content?.product_description,
      productSeoTitles: normalizedProduct.seo?.metaTitle
    });
    
    // Görsel ayarla
    if ((product.images && product.images.length > 0) || product.image) {
      setImagePreview(product.images?.[0] || product.image || null);
    } else {
      setImagePreview(null);
    }
    
    setSelectedFiles({});
    setShowProductForm(true);
  };

  // Advantages yönetimi
  const addAdvantage = (lang: 'tr' | 'en' | 'es') => {
    setProductAdvantages(prev => ({
      ...prev,
      [lang]: [...prev[lang], '']
    }));
  };

  const removeAdvantage = (lang: 'tr' | 'en' | 'es', index: number) => {
    setProductAdvantages(prev => ({
      ...prev,
      [lang]: prev[lang].filter((_, i) => i !== index)
    }));
  };

  const updateAdvantage = (lang: 'tr' | 'en' | 'es', index: number, value: string) => {
    setProductAdvantages(prev => ({
      ...prev,
      [lang]: prev[lang].map((item, i) => i === index ? value : item)
    }));
  };


  const handleDeleteProduct = async (product: Product) => {
    if (window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) {
      try {
        const productId = product._id || product.id;
        if (!productId) {
          toast.error('Ürün ID bulunamadı');
          return;
        }
        await productService.deleteProduct(productId);
        toast.success('Ürün başarıyla silindi');
        fetchData();
      } catch (error) {
        console.error('Error deleting product:', error);
        toast.error('Ürün silinirken hata oluştu');
      }
    }
  };


  // Category form submit
  const handleCategorySubmit = async (formData: FormData) => {
    try {
      setSubmitting(true);
      
      // Debug: FormData içeriğini kontrol et
      console.log('=== CATEGORY FORM DEBUG ===');
      console.log('FormData entries:');
      console.log('title_tr:', formData.get('title_tr'));
      console.log('title_en:', formData.get('title_en'));
      console.log('title_es:', formData.get('title_es'));
      console.log('description_tr:', formData.get('description_tr'));
      console.log('description_en:', formData.get('description_en'));
      console.log('description_es:', formData.get('description_es'));
      console.log('Current formTitles:', formTitles);
      console.log('Current formDescriptions:', formDescriptions);
      
      const categoryData = {
        title: {
          tr: formData.get('title_tr') as string || formTitles.tr || '',
          en: formData.get('title_en') as string || formTitles.en || '',
          es: formData.get('title_es') as string || formTitles.es || '',
        },
        slug: {
          tr: formData.get('slug_tr') as string || formSlugs.tr || '',
          en: formData.get('slug_en') as string || formSlugs.en || '',
          es: formData.get('slug_es') as string || formSlugs.es || '',
        },
        description: {
          tr: formData.get('description_tr') as string || formDescriptions.tr || '',
          en: formData.get('description_en') as string || formDescriptions.en || '',
          es: formData.get('description_es') as string || formDescriptions.es || '',
        },
        // Mevcut görseli koru (yeni görsel yoksa)
        image: editingCategory?.image || "string",
        order: categoryOrder
      };
      
      console.log('Final categoryData:', categoryData);

      // Validasyon: TR başlığı zorunlu
      if (!categoryData.title.tr || categoryData.title.tr.trim() === '') {
        toast.error('Türkçe kategori başlığı zorunludur!');
        setSubmitting(false);
        return;
      }

      // Aynı isimde kategori kontrolü
      const existingCategory = categories.find(cat => {
        // Düzenleme modundaysa mevcut kategoriyi hariç tut
        if (editingCategory && (cat.id === editingCategory.id || cat._id === editingCategory._id)) {
          return false;
        }
        // TR başlığını karşılaştır (case insensitive)
        const existingTitle = (typeof cat.title === 'string' ? cat.title : cat.title?.tr || '').toLowerCase().trim();
        const newTitle = categoryData.title.tr.toLowerCase().trim();
        return existingTitle === newTitle;
      });

      if (existingCategory) {
        toast.error('Bu isimde bir kategori zaten mevcut!');
        setSubmitting(false);
        return;
      }

      let categoryId;
      if (editingCategory) {
        const id = editingCategory.id || editingCategory._id;
        if (!id) {
          toast.error('Kategori ID bulunamadı');
          return;
        }
        await categoryService.updateCategory(id, categoryData);
        categoryId = id;
        toast.success('Kategori başarıyla güncellendi');
      } else {
        const result = await categoryService.createCategory(categoryData);
        categoryId = result._id || result.id;
        toast.success('Kategori başarıyla oluşturuldu');
      }

      // Görsel yükleme
      if (categoryId && selectedFiles.image) {
        console.log('Uploading category image...');
        toast.loading('Görsel yükleniyor...', { id: 'category-image-upload' });
        try {
          await handleImageUpload(categoryId);
          toast.success('Görsel başarıyla yüklendi', { id: 'category-image-upload' });
        } catch (imageError) {
          toast.error('Görsel yüklenemedi', { id: 'category-image-upload' });
          throw imageError;
        }
      }

      // Cleanup
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
      
      setShowCategoryForm(false);
      setEditingCategory(null);
      setImagePreview(null);
      setSelectedFiles({});
      
      fetchData();
    } catch (error: any) {
      console.error('Error saving category:', error);
      toast.error('Kategori kaydedilirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  // Product form submit
  const handleProductSubmit = async (formData: FormData) => {
    try {
      setSubmitting(true);
      
      if (!selectedCategory) {
        toast.error('Kategori seçilmedi');
        return;
      }

      // TR başlığı zorunlu kontrolü
      if (!productTitles.tr || productTitles.tr.trim() === '') {
        toast.error('Türkçe ürün başlığı zorunludur!');
        setSubmitting(false);
        return;
      }

      // Aynı isimde ürün kontrolü (aynı kategori içinde)
      const categoryProducts = getCategoryProducts(selectedCategory?.id || selectedCategory?._id || '');
      const existingProduct = categoryProducts.find(product => {
        // Düzenleme modundaysa mevcut ürünü hariç tut
        if (editingProduct && (product.id === editingProduct.id || product._id === editingProduct._id)) {
          return false;
        }
        // TR başlığını karşılaştır (case insensitive)
        const existingTitle = (typeof product.title === 'string' ? product.title : product.title?.tr || '').toLowerCase().trim();
        const newTitle = productTitles.tr.toLowerCase().trim();
        return existingTitle === newTitle;
      });

      if (existingProduct) {
        toast.error('Bu kategoride aynı isimde bir ürün zaten mevcut!');
        setSubmitting(false);
        return;
      }


      // Boş advantages'leri filtrele
      const filteredAdvantages = {
        tr: productAdvantages.tr.filter(adv => adv.trim() !== ''),
        en: productAdvantages.en.filter(adv => adv.trim() !== ''),
        es: productAdvantages.es.filter(adv => adv.trim() !== '')
      };

      const productData = {
        slug: productSlugs, // Multi-language slug
        categoryId: selectedCategory?.id || selectedCategory?._id,
        title: productTitles,
        summary: productSummaries,
        content: {
          product_description: productDescriptions,
          technical_areas: productTechnicalAreas,
          usage_areas: productUsageAreas,
          usage_instructions: productUsageInstructions,
          advantages: filteredAdvantages
        },
        seo: {
          metaTitle: productSeoTitles,
          metaDescription: productSeoDescriptions
        },
        order: productOrder
      };

      // Validation kontrolleri
      if (!productData.categoryId) {
        toast.error('Lütfen bir kategori seçin');
        setSubmitting(false);
        return;
      }

      if (!productData.title.tr || !productData.title.tr.trim()) {
        toast.error('Lütfen Türkçe ürün başlığı girin');
        setSubmitting(false);
        return;
      }

      if (!productData.summary.tr || !productData.summary.tr.trim()) {
        toast.error('Lütfen Türkçe ürün özeti girin');
        setSubmitting(false);
        return;
      }

      console.log('Product data to submit:', productData);
      console.log('Selected category:', selectedCategory);

      let productId;
      if (editingProduct) {
        const id = editingProduct.id || editingProduct._id;
        if (!id) {
          toast.error('Ürün ID bulunamadı');
          return;
        }
        await productService.updateProduct(id, productData);
        productId = id;
        toast.success('Ürün başarıyla güncellendi');
      } else {
        const result = await productService.createProduct(productData);
        productId = result._id || result.id;
        toast.success('Ürün başarıyla oluşturuldu');
      }

      // Akıllı görsel yönetimi
      if (productId) {
        const hasDeletedImages = deletedImages.length > 0;
        const hasNewImages = productImageFiles.length > 0;
        const hasImageChanges = hasDeletedImages || hasNewImages;
        
        if (hasImageChanges) {
          try {
            // Sadece silinen görselleri sil
            if (hasDeletedImages) {
              console.log('Deleting specific images...');
              console.log('Images to delete:', deletedImages);
              toast.loading(`Silinen görseller temizleniyor... (${deletedImages.length})`, { id: 'product-images' });
              
              try {
                // Sadece silinen görselleri bulk delete ile sil
                await productService.bulkDeleteImages(deletedImages);
                console.log('Successfully deleted specific images');
              } catch (deleteError) {
                console.error('Error deleting specific images:', deleteError);
                toast.error('Bazı görseller silinemedi', { id: 'product-images' });
                // Hata durumunda işleme devam et
              }
            }

            // Yeni görselleri yükle
            if (hasNewImages) {
              console.log('Uploading new images...');
              toast.loading(`Yeni görseller yükleniyor... (0/${productImageFiles.length})`, { id: 'product-images' });
              
              for (let i = 0; i < productImageFiles.length; i++) {
                const file = productImageFiles[i];
                if (file) {
                  toast.loading(`Yeni görseller yükleniyor... (${i + 1}/${productImageFiles.length})`, { id: 'product-images' });
                  console.log(`Uploading new image ${i + 1}/${productImageFiles.length}:`, file.name);
                  await productService.uploadProductImage(productId, file);
                }
              }
              toast.success('Yeni görseller başarıyla yüklendi', { id: 'product-images' });
            }

            // Sonuç mesajı
            if (hasDeletedImages && hasNewImages) {
              toast.success('Görseller başarıyla güncellendi', { id: 'product-images' });
            } else if (hasDeletedImages) {
              toast.success('Seçilen görseller silindi', { id: 'product-images' });
            }
          } catch (error) {
            console.error('Error managing product images:', error);
            toast.error('Ürün kaydedildi ancak görsel işlemlerinde sorun oluştu', { id: 'product-images' });
          }
        }
      }

      // Cleanup - çoklu görsel preview'larını temizle
      productImagePreviews.forEach(preview => {
        if (preview && preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview);
        }
      });
      
      // Eski tek görsel cleanup
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
      
      setShowProductForm(false);
      setEditingProduct(null);
      setImagePreview(null);
      setSelectedFiles({});
      
      // Çoklu görsel state'lerini temizle
      setProductImageFiles([]);
      setProductImagePreviews([]);
      setOriginalImages([]);
      setDeletedImages([]);
      
      fetchData();
    } catch (error: any) {
      console.error('Error saving product:', error);
      toast.error('Ürün kaydedilirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseForm = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    
    setShowCategoryForm(false);
    setEditingCategory(null);
    setImagePreview(null);
    setSelectedFiles({});
  };

  // Filtreleme
  const filteredCategories = categories
    .filter(category => {
      const title = typeof category.title === 'string' ? category.title : (category.title?.tr || '');
      return title.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      // Önce order'a göre sırala
      const orderA = a.order ?? 999999;
      const orderB = b.order ?? 999999;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      // Order aynıysa alfabetik sırala
      const titleA = (typeof a.title === 'string' ? a.title : a.title?.tr || '').toLowerCase();
      const titleB = (typeof b.title === 'string' ? b.title : b.title?.tr || '').toLowerCase();
      return titleA.localeCompare(titleB, 'tr');
    });

  const filteredProducts = selectedCategory ? 
    getCategoryProducts(selectedCategory._id || selectedCategory.id || '')
      .filter(product => {
        const title = typeof product.title === 'string' ? product.title : (product.title?.tr || '');
        return title.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .sort((a, b) => {
        // Önce order'a göre sırala
        const orderA = a.order ?? 999999;
        const orderB = b.order ?? 999999;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        // Order aynıysa alfabetik sırala
        const titleA = (typeof a.title === 'string' ? a.title : a.title?.tr || '').toLowerCase();
        const titleB = (typeof b.title === 'string' ? b.title : b.title?.tr || '').toLowerCase();
        return titleA.localeCompare(titleB, 'tr');
      }) : [];

  if (loading) {
    return (
      <MainLayout title="Ürünler ve Kategoriler">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Ürünler ve Kategoriler">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {selectedCategory && (
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Kategoriler
                </button>
              )}
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {selectedCategory ? 
                    `${typeof selectedCategory.title === 'string' ? selectedCategory.title : selectedCategory.title?.tr}` : 
                    'Ürünler & Kategoriler'
                  }
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedCategory ? 
                    `${filteredProducts.length} ürün` : 
                    `${filteredCategories.length} kategori`
                  }
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={selectedCategory ? "Ürün ara..." : "Kategori ara..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              
              {!selectedCategory ? (
                <button
                  onClick={handleNewCategory}
                  className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Yeni Kategori
                </button>
              ) : (
                <button
                  onClick={handleNewProduct}
                  className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Yeni Ürün
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        {!selectedCategory ? (
          /* Categories List */
          <CategoryList
            categories={filteredCategories.map(cat => ({
              ...cat,
              productCount: getCategoryProducts(cat._id || cat.id || '').length
            }))}
            loading={loading}
            onEdit={handleEditCategory}
            onDelete={handleDeleteCategory}
            onUpdateOrder={handleUpdateCategoryOrder}
            onSelect={setSelectedCategory}
            selectedCategory={selectedCategory}
          />
        ) : (
          /* Products Table */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Ürün Listesi</h2>
            </div>
            
            <ProductTable
              products={filteredProducts}
              loading={loading}
              onEdit={handleEditProduct}
              onDelete={handleDeleteProduct}
              onUpdateOrder={handleUpdateProductOrder}
            />
          </div>
        )}

        {/* Category Form Modal */}
        {showCategoryForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
              {/* Loading Overlay */}
              {submitting && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">İşlem yapılıyor, lütfen bekleyin...</p>
                  </div>
                </div>
              )}
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori'}
                </h2>
                <button
                  onClick={handleCloseForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleCategorySubmit(formData);
              }} className="p-6 space-y-6">
                
                {/* Language Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Düzenleme Dili
                  </label>
                  <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                    {[
                      { code: 'tr', name: 'Türkçe' },
                      { code: 'en', name: 'English' },
                      { code: 'es', name: 'Español' }
                    ].map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setSelectedLanguage(lang.code as 'tr' | 'en' | 'es')}
                        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          selectedLanguage === lang.code
                            ? 'bg-white text-primary-700 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kategori Adı ({selectedLanguage.toUpperCase()})
                    {selectedLanguage === 'tr' && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    type="text"
                    name={`title_${selectedLanguage}`}
                    value={formTitles[selectedLanguage] || ''}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setFormTitles(prev => ({
                        ...prev,
                        [selectedLanguage]: newValue
                      }));
                      
                      // Auto-generate slug from title
                      if (newValue) {
                        const autoSlug = categoryService.createSlug(newValue);
                        setFormSlugs(prev => ({
                          ...prev,
                          [selectedLanguage]: autoSlug
                        }));
                      }
                      
                      // TR dili için real-time kontrol
                      if (selectedLanguage === 'tr') {
                        checkCategoryNameDuplicate(newValue);
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                      selectedLanguage === 'tr' && ((!formTitles.tr || formTitles.tr.trim() === '') || categoryNameError)
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-primary-500'
                    }`}
                    placeholder={selectedLanguage === 'tr' ? "Kategori adını girin (zorunlu)" : "Kategori adını girin"}
                    required={selectedLanguage === 'tr'}
                  />
                  
                  {/* Error mesajları */}
                  {selectedLanguage === 'tr' && (
                    <>
                      {(!formTitles.tr || formTitles.tr.trim() === '') && (
                        <p className="text-red-500 text-sm mt-1">
                          Türkçe kategori adı zorunludur
                        </p>
                      )}
                      {categoryNameError && (
                        <p className="text-red-500 text-sm mt-1">
                          {categoryNameError}
                        </p>
                      )}
                    </>
                  )}
                  
                  {/* Hidden inputs for other languages */}
                  {['tr', 'en', 'es'].map(lang => (
                    lang !== selectedLanguage && (
                      <input
                        key={lang}
                        type="hidden"
                        name={`title_${lang}`}
                        value={formTitles[lang as 'tr' | 'en' | 'es'] || ''}
                      />
                    )
                  ))}
                </div>

                {/* Slug */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL Slug ({selectedLanguage.toUpperCase()})
                    <span className="text-xs text-gray-500 ml-2">(Otomatik oluşturulur, düzenlenebilir)</span>
                  </label>
                  <input
                    type="text"
                    name={`slug_${selectedLanguage}`}
                    value={formSlugs[selectedLanguage] || ''}
                    onChange={(e) => setFormSlugs(prev => ({
                      ...prev,
                      [selectedLanguage]: e.target.value
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                    placeholder={`${selectedLanguage}-slug-ornegi`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Başlık girildiğinde otomatik oluşturulur. Özelleştirmek için düzenleyebilirsiniz.
                  </p>
                  
                  {/* Hidden inputs for other languages */}
                  {['tr', 'en', 'es'].map(lang => (
                    lang !== selectedLanguage && (
                      <input
                        key={lang}
                        type="hidden"
                        name={`slug_${lang}`}
                        value={formSlugs[lang as 'tr' | 'en' | 'es'] || ''}
                      />
                    )
                  ))}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Açıklama ({selectedLanguage.toUpperCase()})
                  </label>
                  <textarea
                    rows={4}
                    name={`description_${selectedLanguage}`}
                    value={formDescriptions[selectedLanguage] || ''}
                    onChange={(e) => setFormDescriptions(prev => ({
                      ...prev,
                      [selectedLanguage]: e.target.value
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Kategori açıklamasını girin"
                    required
                  />
                  
                  {/* Hidden inputs for other languages */}
                  {['tr', 'en', 'es'].map(lang => (
                    lang !== selectedLanguage && (
                      <input
                        key={lang}
                        type="hidden"
                        name={`description_${lang}`}
                        value={formDescriptions[lang as 'tr' | 'en' | 'es'] || ''}
                      />
                    )
                  ))}
                </div>

                {/* Order */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sıra Numarası
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={categoryOrder}
                    onChange={(e) => setCategoryOrder(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Küçük numara önce gösterilir (örn: 1, 2, 3...)
                  </p>
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kategori Görseli
                  </label>
                  
                  {imagePreview ? (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Category preview"
                        className="w-full h-48 object-cover rounded-lg border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => document.getElementById('category-image-input')?.click()}
                      className="w-full h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 hover:bg-primary-50"
                    >
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Görsel yüklemek için tıklayın</p>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF (Max 5MB)</p>
                    </div>
                  )}
                  
                  <input
                    id="category-image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || categoryNameError !== ''}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {editingCategory ? 'Güncelle' : 'Oluştur'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Product Form Modal */}
        {showProductForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[95vh] flex flex-col relative">
              {/* Loading Overlay */}
              {submitting && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">İşlem yapılıyor, lütfen bekleyin...</p>
                  </div>
                </div>
              )}
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <Package className="h-6 w-6 text-primary-600" />
                  <h2 className="text-xl font-semibold text-gray-900">
                    {editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
                  </h2>
                </div>
                <button
                  onClick={() => setShowProductForm(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleProductSubmit(formData);
              }} className="flex flex-col flex-1 min-h-0">
                
                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-6">
                
                {/* Language Selector */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Düzenleme Dili
                  </label>
                  <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg max-w-md">
                    {[
                      { code: 'tr', name: 'Türkçe' },
                      { code: 'en', name: 'English' },
                      { code: 'es', name: 'Español' }
                    ].map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setSelectedLanguage(lang.code as 'tr' | 'en' | 'es')}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex-1 ${
                          selectedLanguage === lang.code
                            ? 'bg-white text-primary-700 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Sol Kolon - Temel Bilgiler */}
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <FileText className="h-4 w-4 mr-2 text-primary-600" />
                        Temel Bilgiler
                      </h3>

                      {/* Title */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Ürün Adı ({selectedLanguage.toUpperCase()})
                          {selectedLanguage === 'tr' && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                          type="text"
                          name={`title_${selectedLanguage}`}
                          value={productTitles[selectedLanguage] || ''}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            setProductTitles(prev => ({
                              ...prev,
                              [selectedLanguage]: newValue
                            }));
                            // Slug'ı otomatik oluştur
                            setProductSlugs(prev => ({
                              ...prev,
                              [selectedLanguage]: createSlug(newValue)
                            }));
                            // TR dili için real-time kontrol
                            if (selectedLanguage === 'tr') {
                              checkProductNameDuplicate(newValue);
                            }
                          }}
                          className={`w-full px-2 py-1.5 text-sm border rounded-md focus:ring-1 focus:border-transparent ${
                            selectedLanguage === 'tr' && ((!productTitles.tr || productTitles.tr.trim() === '') || productNameError)
                              ? 'border-red-300 focus:ring-red-500'
                              : 'border-gray-300 focus:ring-primary-500'
                          }`}
                          placeholder={selectedLanguage === 'tr' ? "Ürün adını girin (zorunlu)" : "Ürün adını girin"}
                          required={selectedLanguage === 'tr'}
                        />
                        
                        {/* Error mesajları */}
                        {selectedLanguage === 'tr' && (
                          <>
                            {(!productTitles.tr || productTitles.tr.trim() === '') && (
                              <p className="text-red-500 text-sm mt-1">
                                Türkçe ürün adı zorunludur
                              </p>
                            )}
                            {productNameError && (
                              <p className="text-red-500 text-sm mt-1">
                                {productNameError}
                              </p>
                            )}
                          </>
                        )}
                        
                        {/* Hidden inputs for other languages */}
                        {['tr', 'en', 'es'].map(lang => (
                          lang !== selectedLanguage && (
                            <input
                              key={lang}
                              type="hidden"
                              name={`title_${lang}`}
                              value={productTitles[lang as 'tr' | 'en' | 'es'] || ''}
                            />
                          )
                        ))}
                      </div>

                      {/* Slug */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          URL Slug ({selectedLanguage.toUpperCase()})
                          <span className="text-gray-500 text-xs ml-1">(Otomatik oluşturulur, düzenlenebilir)</span>
                        </label>
                        <input
                          type="text"
                          value={productSlugs[selectedLanguage] || ''}
                          onChange={(e) => setProductSlugs(prev => ({
                            ...prev,
                            [selectedLanguage]: e.target.value
                          }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent bg-gray-50"
                          placeholder={`${selectedLanguage}-slug-ornek`}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Ürün adından otomatik oluşturulur. Türkçe karakterler İngilizce'ye çevrilir.
                        </p>
                        
                        {/* Hidden inputs for other languages */}
                        {['tr', 'en', 'es'].map(lang => (
                          lang !== selectedLanguage && (
                            <input
                              key={lang}
                              type="hidden"
                              name={`slug_${lang}`}
                              value={productSlugs[lang as 'tr' | 'en' | 'es'] || ''}
                            />
                          )
                        ))}
                      </div>

                      {/* Summary */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Ürün Özeti ({selectedLanguage.toUpperCase()})
                        </label>
                        <textarea
                          rows={2}
                          name={`summary_${selectedLanguage}`}
                          value={productSummaries[selectedLanguage] || ''}
                          onChange={(e) => setProductSummaries(prev => ({
                            ...prev,
                            [selectedLanguage]: e.target.value
                          }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Kısa ürün özetini girin"
                        />
                        
                        {/* Hidden inputs for other languages */}
                        {['tr', 'en', 'es'].map(lang => (
                          lang !== selectedLanguage && (
                            <textarea
                              key={lang}
                              name={`summary_${lang}`}
                              value={productSummaries[lang as 'tr' | 'en' | 'es'] || ''}
                              style={{ display: 'none' }}
                              readOnly
                            />
                          )
                        ))}
                      </div>
                    </div>

                    {/* Detaylı İçerik Alanları */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <BookOpen className="h-4 w-4 mr-2 text-primary-600" />
                        Detaylı İçerik
                      </h3>

                      {/* Product Description */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Ürün Açıklaması ({selectedLanguage.toUpperCase()})
                        </label>
                        <textarea
                          rows={3}
                          value={productDescriptions[selectedLanguage] || ''}
                          onChange={(e) => setProductDescriptions(prev => ({
                            ...prev,
                            [selectedLanguage]: e.target.value
                          }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Ürünün detaylı açıklamasını girin"
                        />
                      </div>

                      {/* Technical Areas */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Teknik Özellikler ({selectedLanguage.toUpperCase()})
                        </label>
                        <textarea
                          rows={2}
                          value={productTechnicalAreas[selectedLanguage] || ''}
                          onChange={(e) => setProductTechnicalAreas(prev => ({
                            ...prev,
                            [selectedLanguage]: e.target.value
                          }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Teknik özellikleri girin (virgülle ayırın)"
                        />
                      </div>

                      {/* Usage Areas */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Kullanım Alanları ({selectedLanguage.toUpperCase()})
                        </label>
                        <textarea
                          rows={2}
                          value={productUsageAreas[selectedLanguage] || ''}
                          onChange={(e) => setProductUsageAreas(prev => ({
                            ...prev,
                            [selectedLanguage]: e.target.value
                          }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Kullanım alanlarını girin (virgülle ayırın)"
                        />
                      </div>

                      {/* Usage Instructions */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Kullanım Talimatları ({selectedLanguage.toUpperCase()})
                        </label>
                        <textarea
                          rows={2}
                          value={productUsageInstructions[selectedLanguage] || ''}
                          onChange={(e) => setProductUsageInstructions(prev => ({
                            ...prev,
                            [selectedLanguage]: e.target.value
                          }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Kullanım talimatlarını girin"
                        />
                      </div>

                      {/* Advantages */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Avantajlar ({selectedLanguage.toUpperCase()})
                        </label>
                        <div className="space-y-1.5">
                          {productAdvantages[selectedLanguage].map((advantage, index) => (
                            <div key={index} className="flex gap-1.5">
                              <input
                                type="text"
                                value={advantage}
                                onChange={(e) => updateAdvantage(selectedLanguage, index, e.target.value)}
                                className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                                placeholder={`Avantaj ${index + 1}`}
                              />
                              {productAdvantages[selectedLanguage].length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeAdvantage(selectedLanguage, index)}
                                  className="px-2 py-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addAdvantage(selectedLanguage)}
                            className="w-full px-2 py-1.5 text-xs text-primary-600 border border-primary-300 rounded-md hover:bg-primary-50 flex items-center justify-center"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Avantaj Ekle
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sağ Kolon - SEO */}
                  <div className="space-y-4">

                    {/* Sıralama */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <Package className="h-4 w-4 mr-2 text-primary-600" />
                        Sıralama
                      </h3>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Sıra Numarası
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={productOrder}
                          onChange={(e) => setProductOrder(parseInt(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                          placeholder="0"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Küçük numara önce gösterilir (örn: 1, 2, 3...)
                        </p>
                      </div>
                    </div>

                    {/* SEO */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <Target className="h-4 w-4 mr-2 text-primary-600" />
                        SEO Ayarları
                      </h3>

                      {/* SEO Title */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          SEO Başlığı ({selectedLanguage.toUpperCase()})
                        </label>
                        <input
                          type="text"
                          value={productSeoTitles[selectedLanguage] || ''}
                          onChange={(e) => setProductSeoTitles(prev => ({
                            ...prev,
                            [selectedLanguage]: e.target.value
                          }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                          placeholder="SEO için optimize edilmiş başlık"
                        />
                      </div>

                      {/* SEO Description */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          SEO Açıklaması ({selectedLanguage.toUpperCase()})
                        </label>
                        <textarea
                          rows={2}
                          value={productSeoDescriptions[selectedLanguage] || ''}
                          onChange={(e) => setProductSeoDescriptions(prev => ({
                            ...prev,
                            [selectedLanguage]: e.target.value
                          }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Arama motorları için açıklama (150-160 karakter)"
                        />
                      </div>
                    </div>

                    {/* Multiple Image Upload */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                          <ImageIcon className="h-4 w-4 mr-2 text-primary-600" />
                          Ürün Görselleri
                        </h3>
                        <button
                          type="button"
                          onClick={addNewImageSlot}
                          className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-md hover:bg-primary-200 flex items-center"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Görsel Ekle
                        </button>
                      </div>
                      
                      <div className="space-y-2">
                        {productImagePreviews.map((preview, index) => (
                          <div key={index} className="border border-gray-200 rounded-md p-2">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-gray-700">
                                {index + 1}. Görsel
                              </span>
                              {productImagePreviews.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveProductImage(index)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                            
                            {preview ? (
                              <div className="relative">
                                <img
                                  src={preview}
                                  alt={`Product preview ${index + 1}`}
                                  className="w-full h-24 object-cover rounded border border-gray-300"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemoveProductImage(index)}
                                  className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 shadow-lg"
                                >
                                  <X className="h-2 w-2" />
                                </button>
                              </div>
                            ) : (
                              <div
                                onClick={() => document.getElementById(`product-image-input-${index}`)?.click()}
                                className="w-full h-24 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors"
                              >
                                <Upload className="h-4 w-4 text-gray-400 mb-1" />
                                <p className="text-xs text-gray-600">Görsel seçin</p>
                              </div>
                            )}
                            
                            <input
                              id={`product-image-input-${index}`}
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleProductImageSelect(e, index)}
                              className="hidden"
                            />
                          </div>
                        ))}
                        
                        {productImagePreviews.length === 0 && (
                          <div
                            onClick={addNewImageSlot}
                            className="w-full h-32 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors"
                          >
                            <Upload className="h-6 w-6 text-gray-400 mb-1" />
                            <p className="text-xs text-gray-600 font-medium">İlk görseli eklemek için tıklayın</p>
                            <p className="text-xs text-gray-500">PNG, JPG, GIF (Max 5MB)</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                </div>

                {/* Fixed Form Actions */}
                <div className="flex justify-between items-center border-t border-gray-200 bg-gray-50 px-4 py-3 rounded-b-lg flex-shrink-0">
                  <div className="text-xs text-gray-600">
                    <span className="text-red-500">*</span> Türkçe alanlar zorunludur
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowProductForm(false)}
                      className="px-4 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      İptal
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || productNameError !== ''}
                      className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1.5"></div>
                          Kaydediliyor...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-3 w-3 mr-1.5" />
                          {editingProduct ? 'Güncelle' : 'Oluştur'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ProductsCategories;

